import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Drawer, Form, Input, Select, Radio } from "antd";
import { ColumnProps } from "antd/lib/table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormInstance } from "antd/lib/form";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { observer } from "mobx-react";
import { emailInboxRuleStore } from "../../Stores/EmailInboxRuleStore";
import { emailInboxFilterStore } from "../../Stores/EmailInboxFilterStore";
import { emailInboxListStore } from "../../Stores/EmailInboxListStore";
import { Button } from "Components/General/Button";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { RulesDnD } from "./RulesDnD";
import classNames from "classnames";
import { modal } from "Components/General/Modal/AntdModal";
import { Table } from "Components/General/Table";

interface RulesModalProps extends WithTranslation, ModalComponentProps {}
interface RulesModalState {
  editingId: number;
  visible: boolean;
  dndMode: boolean;
}

export const RulesModal = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<RulesModalProps, RulesModalState> {
      formRef: React.RefObject<FormInstance> = React.createRef();
      constructor(props: RulesModalProps) {
        super(props);
        this.state = {
          editingId: null,
          visible: false,
          dndMode: true,
        };
      }

      componentDidMount() {
        (async () => {
          // NOTE: Order of load is crucial for RulesDnD.tsx
          await Promise.all([emailInboxListStore.fetch(), emailInboxFilterStore.fetch()]);
          await emailInboxRuleStore.fetch();
        })();
      }

      componentWillUnmount() {
        emailInboxRuleStore.reset();
      }

      showDrawer = (record: API_MailInboxRule) => {
        this.setState({ visible: true, editingId: record.id }, () => this.formRef.current.setFieldsValue(record));
        this.props.setConfirmOnCancel(true);
      };

      createRule = () => {
        this.setState({ visible: true });
        this.props.setConfirmOnCancel(true);
      };

      onClose = () => {
        this.setState({ visible: false, editingId: null }, () => this.formRef.current.resetFields());
        this.props.setConfirmOnCancel(false);
      };

      confirmDelete = (ruleId: number) => {
        const { t } = this.props;
        const onOk = async () => {
          await emailInboxRuleStore.deleteItem(ruleId);
          notificationSuccess(t("Success"), t("Rule deleted"));
        };
        modal.confirm({
          title: t("Delete rule?"),
          onOk,
          icon: <ExclamationCircleOutlined />,
          okText: t("Yes"),
          okType: "default",
          cancelButtonProps: { type: "primary" },
          cancelText: t("No"),
          zIndex: 1052,
        });
      };

      handleFinish = async (values) => {
        const { t } = this.props;
        const { editingId } = this.state;
        try {
          if (editingId) {
            await emailInboxRuleStore.editItem(editingId, values);
            notificationSuccess(t("Success"), t("Rule edited"));
          } else {
            await emailInboxRuleStore.createItem(values);
            notificationSuccess(t("Success"), t("Rule created"));
          }
          this.onClose();
        } catch (error) {
          throw error;
        }
      };

      changeMode = (e) => {
        this.setState({ dndMode: e.target.value });
      };

      render() {
        const { t } = this.props;
        const columns: ColumnProps<API_MailInboxRule>[] = [
          {
            dataIndex: "name",
            title: t("Name"),
          },
          {
            dataIndex: "description",
            title: t("Description"),
          },
          {
            dataIndex: "filterId",
            title: t("Filter"),
            render: (_, rec) => emailInboxFilterStore.getItem(rec.filterId)?.name,
          },
          {
            dataIndex: "inboxes",
            title: t("Inboxes"),
            render: (_, rec) => rec.inboxes.map((inboxId) => emailInboxListStore.getItem(inboxId)?.name).join(", "),
          },
          {
            dataIndex: "id",
            title: t("Actions"),
            width: 110,
            render: (_, rec) => (
              <>
                <Button size="small" type="link" onClick={() => this.showDrawer(rec)}>
                  <FontAwesomeIcon icon={["far", "edit"]} fixedWidth={true} />
                </Button>
                <Button size="small" type="link" onClick={() => this.confirmDelete(rec.id)}>
                  <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} />
                </Button>
              </>
            ),
          },
        ];

        const radioOptions = [
          { label: t("Drag&Drop"), value: true },
          { label: t("Table"), value: false },
        ];

        return (
          <div style={{ display: "flex", height: "100%", flexDirection: "column" }}>
            <ButtonsRow justify="space-between" align="middle" style={{ marginBottom: 10 }}>
              <div>
                <Radio.Group
                  options={radioOptions}
                  optionType="button"
                  value={this.state.dndMode}
                  onChange={this.changeMode}
                />
              </div>
              <Button
                type="primary"
                className={classNames({ "is-hidden": this.state.dndMode })}
                faIcon={["far", "plus"]}
                onClick={this.createRule}
              >
                {t("Add Rule")}
              </Button>
            </ButtonsRow>
            {this.state.dndMode ? (
              <RulesDnD rulesLoaded={emailInboxRuleStore.state === "DONE"} />
            ) : (
              <div>
                <Table
                  name="rulesModal"
                  rowKey="id"
                  columns={columns}
                  loading={emailInboxRuleStore.state === "LOADING"}
                  dataSource={emailInboxRuleStore.list}
                  pagination={{ hideOnSinglePage: true }}
                />
                <Drawer
                  destroyOnHidden={true}
                  title={t("Edit rule")}
                  placement="right"
                  closable={false}
                  onClose={this.onClose}
                  open={this.state.visible}
                  getContainer={() => document.querySelector(".ant-modal-container")}
                  style={{ position: "absolute" }}
                  width={400}
                  footer={
                    <div className="text-right">
                      <Button
                        type="primary"
                        loading={emailInboxRuleStore.actionState === "LOADING"}
                        onClick={() => this.formRef.current.submit()}
                      >
                        {t("Save")}
                      </Button>
                    </div>
                  }
                >
                  <Form ref={this.formRef} onFinish={this.handleFinish} layout="vertical">
                    <Form.Item
                      label={t("Name")}
                      name="name"
                      rules={[{ required: true, message: t("This field is required") }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      label={t("Filter")}
                      name="filterId"
                      rules={[{ required: true, message: t("This field is required") }]}
                    >
                      <Select>
                        {emailInboxFilterStore.data.map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      label={t("Inboxes")}
                      name="inboxes"
                      rules={[{ required: true, message: t("This field is required") }]}
                    >
                      <Select mode="multiple">
                        {emailInboxListStore.data.map((item) => (
                          <Select.Option key={item.id} value={item.id}>
                            {item.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Form>
                </Drawer>
              </div>
            )}
          </div>
        );
      }
    }
  )
);
